import React, { useState, useEffect, useCallback } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../../../config/api';
import AdminCampingLocations from './AdminCampingLocations';
import AdminCampingSites from './AdminCampingSites';
import AdminCampingBookings from './AdminCampingBookings';
import AdminSidebar from '../../../components/admin/AdminSidebar';
import AdminHeader from '../../../components/admin/AdminHeader';

const AdminCamping = () => {
    const [activeTab, setActiveTab] = useState('stats');
    const [stats, setStats] = useState({
        totalLocations: 0,
        totalSites: 0,
        totalBookings: 0,
        totalRevenue: 0,
        recentBookings: [],
        popularLocations: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab from URL
    useEffect(() => {
        const path = location.pathname.split('/').pop();
        if (path === 'camping' || path === 'stats') {
            setActiveTab('stats');
        } else if (path === 'locations') {
            setActiveTab('locations');
        } else if (path === 'sites') {
            setActiveTab('sites');
        } else if (path === 'bookings') {
            setActiveTab('bookings');
        }
    }, [location]);

    // Fetch dashboard stats
    const fetchDashboardStats = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const headers = {
                'Authorization': `Bearer ${token}`,
                'x-auth-token': token
            };

            const [locationsResponse, sitesResponse, bookingsResponse, recentBookingsResponse] = await Promise.all([
                axios.get(buildApiUrl('/camping/locations'), { headers }),
                axios.get(buildApiUrl('/camping/sites'), { headers }),
                axios.get(buildApiUrl('/camping/admin/bookings/stats'), { headers }),
                axios.get(buildApiUrl('/camping/admin/bookings?limit=5'), { headers })
            ]);

            if (bookingsResponse.data.success) {
                const statsData = bookingsResponse.data.data || {};
                const locations = locationsResponse.data?.data || locationsResponse.data || [];
                const sites = sitesResponse.data?.data || sitesResponse.data || [];
                const recentBookingsPayload = recentBookingsResponse.data?.data || {};
                const recentBookings = Array.isArray(recentBookingsPayload.bookings)
                    ? recentBookingsPayload.bookings
                    : [];

                setStats({
                    totalLocations: Array.isArray(locations) ? locations.length : 0,
                    totalSites: Array.isArray(sites) ? sites.length : 0,
                    totalBookings: Number(statsData.total || 0),
                    totalRevenue: Number(statsData.totalRevenue || 0),
                    recentBookings,
                    popularLocations: []
                });
                setError(null);
            }
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            setError('Failed to load dashboard statistics');
            
            // Fallback data for development
            setStats({
                totalLocations: 8,
                totalSites: 24,
                totalBookings: 156,
                totalRevenue: 452000,
                recentBookings: [
                    { id: 1, customer: 'Rahul Sharma', site: 'Glacier View Tent', amount: 4500, status: 'confirmed' },
                    { id: 2, customer: 'Priya Singh', site: 'Meadow View Tent', amount: 3800, status: 'pending' },
                    { id: 3, customer: 'Amit Kumar', site: 'Riverside Glamping', amount: 8500, status: 'completed' },
                    { id: 4, customer: 'Neha Gupta', site: 'Deluxe Cabin', amount: 7200, status: 'confirmed' },
                    { id: 5, customer: 'Vikram Mehta', site: 'Family Tent', amount: 5200, status: 'cancelled' }
                ],
                popularLocations: [
                    { name: 'Gulmarg', bookings: 45, revenue: 180000 },
                    { name: 'Pahalgam', bookings: 38, revenue: 152000 },
                    { name: 'Sonmarg', bookings: 32, revenue: 128000 },
                    { name: 'Betaab Valley', bookings: 28, revenue: 112000 },
                    { name: 'Doodhpathri', bookings: 22, revenue: 88000 }
                ]
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);

    const tabs = [
        { id: 'stats', label: 'Dashboard', icon: 'chart-pie', path: '/admin/camping' },
        { id: 'locations', label: 'Locations', icon: 'map-marker-alt', path: '/admin/camping/locations' },
        { id: 'sites', label: 'Camping Sites', icon: 'campground', path: '/admin/camping/sites' },
        { id: 'bookings', label: 'Bookings', icon: 'calendar-check', path: '/admin/camping/bookings' }
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            'confirmed': 'badge-success',
            'pending': 'badge-warning',
            'cancelled': 'badge-danger',
            'completed': 'badge-info'
        };
        return classes[status] || 'badge-secondary';
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Camping Management" />
                
                <div className="admin-camping">
                    {/* Header Section */}
                    <div className="admin-camping-header">
                        <div>
                            <h1>
                                <i className="fas fa-campground"></i>
                                Camping Management
                            </h1>
                            <p className="header-subtitle">Manage camping locations, sites, and bookings</p>
                        </div>
                        <div className="header-actions">
                            <button 
                                className="btn-outline"
                                onClick={() => navigate('/admin/camping/locations/new')}
                            >
                                <i className="fas fa-map-marker-alt"></i> Add Location
                            </button>
                            <button 
                                className="btn-primary"
                                onClick={() => navigate('/admin/camping/sites/new')}
                            >
                                <i className="fas fa-plus"></i> Add New Site
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats Cards (visible only on dashboard) */}
                    {activeTab === 'stats' && (
                        <div className="quick-stats-grid">
                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                                    <i className="fas fa-map-marker-alt"></i>
                                </div>
                                <div className="stat-content">
                                    <h3>Total Locations</h3>
                                    <p className="stat-number">{stats.totalLocations}</p>
                                </div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: '#e8f5e8', color: '#2e7d32' }}>
                                    <i className="fas fa-campground"></i>
                                </div>
                                <div className="stat-content">
                                    <h3>Total Sites</h3>
                                    <p className="stat-number">{stats.totalSites}</p>
                                </div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: '#fff3e0', color: '#ed6c02' }}>
                                    <i className="fas fa-calendar-check"></i>
                                </div>
                                <div className="stat-content">
                                    <h3>Total Bookings</h3>
                                    <p className="stat-number">{stats.totalBookings}</p>
                                </div>
                            </div>

                            <div className="stat-card">
                                <div className="stat-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}>
                                    <i className="fas fa-rupee-sign"></i>
                                </div>
                                <div className="stat-content">
                                    <h3>Total Revenue</h3>
                                    <p className="stat-number">{formatCurrency(stats.totalRevenue)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tabs Navigation */}
                    <div className="admin-camping-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    navigate(tab.path);
                                }}
                            >
                                <i className={`fas fa-${tab.icon}`}></i>
                                {tab.label}
                                {tab.id === 'bookings' && stats.totalBookings > 0 && (
                                    <span className="tab-badge">{stats.totalBookings}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Dashboard Content - Only show when on stats tab */}
                    {activeTab === 'stats' && (
                        <div className="dashboard-content">
                            {loading ? (
                                <div className="loading-state">
                                    <div className="spinner"></div>
                                    <p>Loading dashboard data...</p>
                                </div>
                            ) : error ? (
                                <div className="error-state">
                                    <i className="fas fa-exclamation-circle"></i>
                                    <p>{error}</p>
                                    <button onClick={fetchDashboardStats} className="btn-primary">
                                        Retry
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Recent Bookings */}
                                    <div className="dashboard-section">
                                        <div className="section-header">
                                            <h3>Recent Bookings</h3>
                                            <Link to="/admin/camping/bookings" className="view-all-link">
                                                View All <i className="fas fa-arrow-right"></i>
                                            </Link>
                                        </div>
                                        <div className="recent-bookings-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Customer</th>
                                                        <th>Site</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stats.recentBookings.map(booking => (
                                                        <tr key={booking.id}>
                                                            <td>
                                                                <strong>{booking.customer}</strong>
                                                            </td>
                                                            <td>{booking.site}</td>
                                                            <td>{formatCurrency(booking.amount)}</td>
                                                            <td>
                                                                <span className={`badge ${getStatusBadgeClass(booking.status)}`}>
                                                                    {booking.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Popular Locations */}
                                    <div className="dashboard-section">
                                        <div className="section-header">
                                            <h3>Popular Locations</h3>
                                        </div>
                                        <div className="popular-locations-list">
                                            {stats.popularLocations.map((location, index) => (
                                                <div key={index} className="location-stat-item">
                                                    <div className="location-info">
                                                        <span className="location-name">{location.name}</span>
                                                        <span className="location-bookings">{location.bookings} bookings</span>
                                                    </div>
                                                    <div className="location-revenue">
                                                        {formatCurrency(location.revenue)}
                                                    </div>
                                                    <div className="progress-bar">
                                                        <div 
                                                            className="progress" 
                                                            style={{ 
                                                                width: `${(location.bookings / Math.max(...stats.popularLocations.map(l => l.bookings))) * 100}%` 
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="dashboard-section quick-actions">
                                        <h3>Quick Actions</h3>
                                        <div className="action-buttons">
                                            <button 
                                                className="action-btn"
                                                onClick={() => navigate('/admin/camping/sites/new')}
                                            >
                                                <i className="fas fa-plus-circle"></i>
                                                Add New Site
                                            </button>
                                            <button 
                                                className="action-btn"
                                                onClick={() => navigate('/admin/camping/locations/new')}
                                            >
                                                <i className="fas fa-map-pin"></i>
                                                Add New Location
                                            </button>
                                            <button 
                                                className="action-btn"
                                                onClick={() => window.open('/camping', '_blank')}
                                            >
                                                <i className="fas fa-eye"></i>
                                                View Public Page
                                            </button>
                                            <button 
                                                className="action-btn"
                                                onClick={() => navigate('/admin/camping/bookings')}
                                            >
                                                <i className="fas fa-calendar-plus"></i>
                                                View All Bookings
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Routes for nested components */}
                    <div className="admin-camping-content">
                        <Routes>
                            <Route path="/" element={null} />
                            <Route path="/stats" element={null} />
                            <Route path="/locations" element={<AdminCampingLocations />} />
                            <Route path="/locations/:id" element={<AdminCampingLocations />} />
                            <Route path="/locations/new" element={<AdminCampingLocations />} />
                            <Route path="/sites" element={<AdminCampingSites />} />
                            <Route path="/sites/:id" element={<AdminCampingSites />} />
                            <Route path="/sites/new" element={<AdminCampingSites />} />
                            <Route path="/bookings" element={<AdminCampingBookings />} />
                            <Route path="/bookings/:id" element={<AdminCampingBookings />} />
                        </Routes>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCamping;
